import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ItemAlreadyExistsError } from '@cbre/http';
import { ReportTemplate } from '@reports/report-list/report-list.model';
import { ExpandableResource } from '@shared/components/tree-data-table/expandable-resource';
import { EntityType } from '@shared/models/entity-enum';
import { Folder } from '@shared/models/folders/folder';
import { hasNoContent, makeTransientFolder } from '@shared/models/folders/helpers';
import { ICatalog, ReportTypes, StaticReportType } from '@shared/models/icommons.model';
import { reportNameExtractor } from '@shared/models/reports/helpers/report-name-extractors';
import { FullQuarterValidator, QuarterSelectorType } from '@cbre/components';
import { LookupCategoryService } from '@shared/services/lookup-category.service';
import { L10nTranslationService } from 'angular-l10n';
import moment from 'moment';
import { Observable } from 'rxjs';
import { distinctUntilChanged, map, tap } from 'rxjs/operators';
import { generateRangeValidators } from './generate-range-validators';
import { LocalFeatureTogglerRepository } from '@cbre/feature-toggles';
import { AppStorageService } from '@shared/services/app-storage.service';
import { updateValues } from '@shared/components/filters-bar/filter-bar.helper';
import { getPropertyTypeLabel } from '@miq/utils';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';

export enum GeoFieldKeys {
  MARKETS = 'markets',
  SUB_MARKET = 'submarkets',
  PROPERTY_TYPES = 'propertyTypes',
  STATISTICAL_PROPERTY_SET = 'statisticalPropertiesSet',
  PROPERTY_SET = 'propertySet',
}

export interface RecordSearch {
  folderId: number | null;
  name: string;
  description: string | null;
  periodType?: string;
  period?: any;
  markets?: GeoOptions[];
  divisions?: GeoOptions[];
  submarkets?: GeoOptions[];
  propertyTypes?: GeoOptions[];
  statisticalPropertiesSet?: GeoOptions[];
  propertySet?: GeoOptions[];
  statuses?: GeoOptions[];
  geoFields?: GeoFields[];
  sizeMin?: { from?: number; to?: number };
  sizeMax?: { from?: number; to?: number };
  date?: { from?: string; to?: string };
  statisticals?: string;
  reportType?: number;
  reportEntityType?: EntityType;
}

export interface SaveSearchDialogData {
  folderStructureResolver: Observable<any[]>;
  onSaveFolderNode: (node) => Observable<any>;
  /**
   * Required when selecting market
   */
  selectInitialPeriod?: boolean;
  defaultPeriodType?: QuarterSelectorType;
  selectFinalPeriod?: boolean;
  geoFields?: ReportFieldsOptions[];
  singleGeoFields?: boolean;
  title: string;
  statuses?: ReportFieldsOptions;
  sizeMin?: ReportFieldsOptions & { from: ReportFieldsOptions; to: ReportFieldsOptions };
  sizeMax?: ReportFieldsOptions & { from: ReportFieldsOptions; to: ReportFieldsOptions };
  date?: ReportFieldsOptions & { from: ReportFieldsOptions; to: ReportFieldsOptions };
  statisticals?: ReportFieldsOptions;
  selectReportType?: boolean;
  shouldEncodeControlName?: boolean;
  selectReportEntity?: boolean;
  templateId?: number;
  entity?: string;
  warningBtnMsg?: string;
}

export interface ReportFieldsOptions {
  key: string;
  uri?: string;
  onload?: boolean;
  data?: GeoOptions[];
  dependent?: string;
  options?: any[];
  required?: boolean;
  /** key used to display translated labels */
  labelKey?: string;
  defaultVal?: string[];
  /** Used for dates or number inputs */
  maxVal?: any;
  /**
   * Logic applied after user input. An example is date formatting.
   */
  postProcessFn?: (...args: any) => any;
  isVisible?: boolean;
}

export interface GeoOptions {
  [x: string]: any;
  key?: number;
  value: ICatalog;
  label?: string;
  selected?: boolean;
}

export interface GeoFields {
  divisions?: GeoOptions[];
  markets?: GeoOptions[];
  submarkets?: GeoOptions[];
  propertyTypes?: GeoOptions[] | GeoOptions;
  statisticalPropertiesSet?: GeoOptions[] | GeoOptions;
  propertySet?: GeoOptions[] | GeoOptions;
}

@Component({
  selector: 'miq-save-search',
  templateUrl: './save-search-dialog.component.html',
  styleUrls: ['./save-search-dialog.component.scss'],
  standalone: false,
})
export class SaveSearchDialogComponent implements OnInit, OnDestroy {
  public selectedFolder: any = null;
  public selectedFolderName: string;
  public showEmptyInputError = false;
  public hasNoContent = hasNoContent;
  public maxDate = new Date();
  public markets = GeoFieldKeys.MARKETS;
  public propertyTypes = GeoFieldKeys.PROPERTY_TYPES;
  public statisticalPropertiesSet: GeoFieldKeys.STATISTICAL_PROPERTY_SET;
  public propertySet: GeoFieldKeys.PROPERTY_SET;
  public lang: string;
  public periodTypeControl: UntypedFormControl;
  public period: UntypedFormControl;
  public nameControl: UntypedFormControl;
  public reportTypeControl: UntypedFormControl;
  public foldersList: Observable<Array<ExpandableResource<Folder>>>;
  public searchFormGroup: UntypedFormGroup;
  public expandableOptions = { childKey: 'data', hasChildrenIndicator: 'isDir' };
  public dialogConfig: SaveSearchDialogData;
  public nodeDataParser = { displayFn: ctx => decodeURIComponent(reportNameExtractor(ctx)) };
  public geoLabelsLocaleKeys = {
    divisions: 'shared.division',
    markets: 'shared.market',
    submarkets: 'shared.submarket',
    propertyTypes: 'recordsSearch.propertyType',
    statisticalPropertiesSet: 'recordsSearch.statisticalPropertySet',
    propertySet: 'recordsSearch.propertySet',
  };
  public reportTypes = [];
  public entity: string;
  public nodes = [];
  public reportEntityTypes = [EntityType.Availability, EntityType.Property];
  private shouldEncodeControlName = true;
  public corporateDialogFields = ['propertyTypes', 'statisticalPropertiesSet', 'propertySet', 'markets'];
  historicalStatChanges = false;
  private periodType = '';
  /**
   * This method returns an array of the ReportTypes enum object and
   * filters only the items with names (keys) ignoring items with values
   * It is used to loop enum items in a template
   */
  public types(): Array<unknown> {
    const reportTypes = ReportTypes[this.entity];
    return reportTypes != null ? Object.keys(reportTypes).filter(key => isNaN(+key)) : [1];
  }

  warningBtnMsg = null;

  constructor(
    private dialogRef: MatDialogRef<SaveSearchDialogComponent>,
    private formBuilder: UntypedFormBuilder,
    @Inject(MAT_DIALOG_DATA) data: SaveSearchDialogData,
    public translationService: L10nTranslationService,
    private lookupCategoryService: LookupCategoryService,
    private ftSrv: LocalFeatureTogglerRepository,
    private appStorageService: AppStorageService,
    @Inject(CbreLoggerToken) private readonly logger: CbreLogger
  ) {
    this.selectedFolderName = this.translationService.translate('recordsSearch.none');
    this.setDialogData(data);
    this.createForms();
  }

  /** ngOnInit required by Angular-l10n */
  ngOnInit() {
    this.fetchFolderStructure();
    if (!!this.dialogConfig.geoFields) {
      this.initGeoValues();
    }
    if (this.dialogConfig.defaultPeriodType) {
      this.periodTypeControl.setValue(this.dialogConfig.defaultPeriodType);
      this.periodTypeChange();
    }
    this.reportTypes = this.types();

    if (this.dialogConfig.selectReportEntity) {
      this.reportTypes.push(EntityType.Property);
      if (!this.dialogConfig.selectReportType) {
        this.reportTypes = this.reportEntityTypes;
      }
    }
    this.subscribeToGeoFieldsChanges();
  }

  ngOnDestroy() {
    // required by angular-l10n
    this.warningBtnMsg = null;
  }
  private subscribeToGeoFieldsChanges() {
    if (
      this.dialogConfig.geoFields &&
      this.historicalStatChanges &&
      this.dialogConfig.templateId === ReportTemplate.CorporateReport
    ) {
      const controls = [GeoFieldKeys.MARKETS, GeoFieldKeys.PROPERTY_SET, GeoFieldKeys.STATISTICAL_PROPERTY_SET].map(
        key => this.searchFormGroup.get(key)
      );

      controls.forEach(control => {
        control?.valueChanges.pipe(distinctUntilChanged()).subscribe(() => {
          this.updateStatisticalPropertiesSetValidation();
        });
      });

      this.updateStatisticalPropertiesSetValidation();
    }
  }

  private setDialogData(data: SaveSearchDialogData) {
    const corporateReports = data.templateId === ReportTemplate.CorporateReport;
    this.warningBtnMsg = data.warningBtnMsg;
    this.historicalStatChanges = corporateReports && this.ftSrv.isFeatureEnabled('historicalStatChanges');
    if (data?.shouldEncodeControlName != null) {
      // configure ure component encoding
      this.shouldEncodeControlName = data.shouldEncodeControlName;
    }

    if (data != null && !!data.folderStructureResolver) {
      this.entity = data.entity;
      this.dialogConfig = {
        folderStructureResolver: data.folderStructureResolver,
        onSaveFolderNode: data.onSaveFolderNode,
        geoFields: data.geoFields,
        singleGeoFields: data.singleGeoFields,
        selectInitialPeriod: !!data.selectInitialPeriod,
        selectFinalPeriod: !!data.selectFinalPeriod,
        defaultPeriodType: data.defaultPeriodType,
        title: data.title,
        statuses: data.statuses,
        sizeMin: data.sizeMin,
        sizeMax: data.sizeMax,
        date: data.date,
        statisticals: data.statisticals,
        selectReportType: data.selectReportType,
        selectReportEntity: data.selectReportEntity,
        templateId: data.templateId,
      };
    } else {
      this.logger.error('Incorrect save search params');
    }
  }

  private createForms() {
    this.nameControl = new UntypedFormControl('', [Validators.required, Validators.maxLength(50)]);
    this.searchFormGroup = this.formBuilder.group({
      name: this.nameControl,
      description: ['', [Validators.maxLength(150)]],
    });

    if (!!this.dialogConfig.geoFields) {
      this.dialogConfig.geoFields.forEach(field => {
        this.searchFormGroup.addControl(
          field.key,
          new UntypedFormControl(null, field.required ? Validators.required : undefined)
        );
      });
    }

    if (this.dialogConfig.selectInitialPeriod) {
      let storedPeriodType;
      if (this.historicalStatChanges) {
        storedPeriodType = this.appStorageService.getItem('periodType');
      }
      this.periodTypeControl = new UntypedFormControl(storedPeriodType || 'quarterly', [Validators.required]);
      this.searchFormGroup.addControl('periodType', this.periodTypeControl);
      this.period = new UntypedFormControl(null, [FullQuarterValidator]);
      if (storedPeriodType === QuarterSelectorType.Yearly && this.historicalStatChanges) {
        this.periodTypeControl.setValue(storedPeriodType);
        this.periodTypeChange();
      }
      this.searchFormGroup.addControl('period', this.period);
    }

    if (this.dialogConfig.selectReportType || this.dialogConfig.selectReportEntity) {
      this.reportTypeControl = new UntypedFormControl('1', [Validators.required]);
      this.searchFormGroup.addControl('reportType', this.reportTypeControl);
    }

    if (this.dialogConfig.statuses) {
      const { statuses } = this.dialogConfig;
      this.searchFormGroup.addControl(
        statuses.key,
        new UntypedFormControl(statuses.defaultVal, statuses.required ? Validators.required : undefined)
      );
    }

    if (this.dialogConfig.statisticals) {
      const { statisticals } = this.dialogConfig;
      this.searchFormGroup.addControl(
        statisticals.key,
        new UntypedFormControl(statisticals.defaultVal, statisticals.required ? Validators.required : undefined)
      );
    }

    /**
     * Generates a common range object with empty form controls
     */
    function genRangeControls(groupOptions: { from: ReportFieldsOptions; to: ReportFieldsOptions }) {
      const { to, from } = groupOptions;
      return {
        [to.key]: [undefined, to.required ? Validators.required : undefined],
        [from.key]: [undefined, from.required ? Validators.required : undefined],
      };
    }

    if (this.dialogConfig.sizeMin) {
      const { sizeMin } = this.dialogConfig;
      const sizeMinControls = genRangeControls(sizeMin);
      this.searchFormGroup.addControl(
        sizeMin.key,
        this.formBuilder.group(sizeMinControls, { validators: generateRangeValidators('from', 'to') })
      );
    }

    if (this.dialogConfig.sizeMax) {
      const { sizeMax } = this.dialogConfig;
      const sizeMaxControls = genRangeControls(sizeMax);
      this.searchFormGroup.addControl(
        sizeMax.key,
        this.formBuilder.group(sizeMaxControls, { validators: generateRangeValidators('from', 'to') })
      );
    }

    if (this.dialogConfig.date) {
      const { date } = this.dialogConfig;
      const datesControls = genRangeControls(date);
      /** Function used to compare between two moments (dates) */
      const compareDatesFn = (val, compareVal) =>
        moment.isMoment(val) && moment.isMoment(compareVal) && val.isAfter(compareVal);

      this.searchFormGroup.addControl(
        date.key,
        this.formBuilder.group(datesControls, {
          validators: generateRangeValidators('from', 'to', compareDatesFn),
        })
      );
    }
  }

  private fetchFolderStructure() {
    if (!!this.dialogConfig.folderStructureResolver) {
      this.foldersList = this.dialogConfig.folderStructureResolver.pipe(
        tap(nodes => (this.nodes = nodes)),
        map(folders => {
          return (folders || []).map(item => new ExpandableResource(item, this.expandableOptions));
        })
      );
    }
  }

  setSelectedFolder($event) {
    if ($event != null) {
      this.selectedFolder = $event;
      this.selectedFolderName = decodeURIComponent(reportNameExtractor($event));
    } else {
      this.selectedFolder = undefined;
      this.selectedFolderName = reportNameExtractor(makeTransientFolder());
    }
  }

  saveNode({ node }) {
    this.dialogConfig.onSaveFolderNode(node).subscribe(
      (id: number) => {
        this.setSelectedFolder({ ...node, id });
        this.fetchFolderStructure();
      },
      errorResponse => {
        if (errorResponse instanceof ItemAlreadyExistsError) {
          return this.onFolderAlreadyExistError(node);
        }
      }
    );
  }

  onFolderAlreadyExistError(node: { name: string }) {
    const existingNode = this.nodes.find(x => x.name === node.name);
    if (existingNode) {
      this.selectedFolder = existingNode;
      this.selectedFolderName = decodeURIComponent(reportNameExtractor(existingNode));
    }
  }

  /**
   * If user changes any geo field value it will checks the whether geo field
   * has dependent attributes or not. If it has dependent attribute, It will
   * fetch the dependent geo attribute data and will resets all the lower order
   * fields data
   */
  public geoValuesChanges({ value }, index: number) {
    if (this.historicalStatChanges) {
      this.updateStatisticalPropertiesSetValidation();
    }
    if (!value || value.length === 0) {
      return;
    }
    const geoValue = Array.isArray(value) ? value : [value];
    const selectedGeoIds: number[] = geoValue.map(option => option.key);
    const dependentGeoIndex: number = this.findDependentGeoIndex(index);
    let dependentGeoField: ReportFieldsOptions;
    if (dependentGeoIndex !== -1) {
      this.resetGeoFields(dependentGeoIndex, 0);
      dependentGeoField = this.dialogConfig.geoFields[dependentGeoIndex];
    }
    if (!!dependentGeoField && !!dependentGeoField.uri && geoValue.length) {
      this.getGeoData(dependentGeoField, selectedGeoIds);
    }
  }

  /**
   * This method to reset selected values and geo options array data when any
   * value was changed by user in higher level field or if user clears the
   * selected values
   */
  public resetGeoFields(index: number, clearIndexAddition: number) {
    const geoData: ReportFieldsOptions[] = this.dialogConfig.geoFields;
    const dependentGeoIndex: number = this.findDependentGeoIndex(index);
    if (dependentGeoIndex !== -1) {
      for (let i = index; i < geoData.length; i++) {
        const clearOptionsFrom: number = i + clearIndexAddition;
        if (geoData[clearOptionsFrom] && !geoData[clearOptionsFrom].data) {
          geoData[clearOptionsFrom].options = [];
          this.searchFormGroup.controls[geoData[i].key].reset();
        } else if (!geoData[i].data) {
          this.searchFormGroup.controls[geoData[i].key].reset();
        }
      }
    } else {
      this.searchFormGroup.controls[geoData[index].key].reset();
    }
  }
  private updateStatisticalPropertiesSetValidation() {
    const controls = {
      markets: this.searchFormGroup.get(GeoFieldKeys.MARKETS),
      propertySet: this.searchFormGroup.get(GeoFieldKeys.PROPERTY_SET),
      statisticalPropertiesSet: this.searchFormGroup.get(GeoFieldKeys.STATISTICAL_PROPERTY_SET),
      propertyTypes: this.searchFormGroup.get(GeoFieldKeys.PROPERTY_TYPES),
    };

    if (!controls.statisticalPropertiesSet || !controls.propertyTypes) return;

    const isEmpty = control => !control?.value || control.value.length === 0;

    const updateValidators = (control, condition, fieldKey) => {
      if (condition) {
        control.setValidators([Validators.required]);
        this.dialogConfig.geoFields.find(field => field.key === fieldKey).required = true;
      } else {
        control.clearValidators();
        this.dialogConfig.geoFields.find(field => field.key === fieldKey).required = false;
      }
      control.updateValueAndValidity();
    };

    updateValidators(
      controls.markets,
      isEmpty(controls.statisticalPropertiesSet) && isEmpty(controls.propertySet),
      GeoFieldKeys.MARKETS
    );
    updateValidators(
      controls.statisticalPropertiesSet,
      isEmpty(controls.markets) && isEmpty(controls.propertySet),
      GeoFieldKeys.STATISTICAL_PROPERTY_SET
    );
    updateValidators(controls.propertyTypes, !isEmpty(controls.markets), GeoFieldKeys.PROPERTY_TYPES);

    this.searchFormGroup.updateValueAndValidity();
  }
  public saveSearch() {
    const nameToValidate = this.nameControl.value != null ? this.nameControl.value : '';
    if (nameToValidate.replace(/\s/g, '').length > 0 && nameToValidate.charAt(0) !== ' ') {
      this.showEmptyInputError = false;
      const formValue = this.searchFormGroup.value;

      if (this.shouldEncodeControlName) {
        // name encoding is only required by reports NOT for saved search (MIQ-23109)
        formValue.name = encodeURIComponent(formValue.name);
      }
      /*
       *  To fix the MIMO report creating issue
       *   line 400 added to remove null value for submarket which is causing an error while creating MIMO report
       */
      formValue.submarkets = formValue.submarkets ?? [];

      const reportTypesByEntity = ReportTypes[this.entity];
      const reportTypeSelection =
        reportTypesByEntity != null ? reportTypesByEntity[formValue.reportType] : StaticReportType;
      const reportType = reportTypeSelection != null ? Number(reportTypeSelection) : StaticReportType;

      const finalValue: RecordSearch = {
        folderId: this.selectedFolder ? this.selectedFolder.id || this.selectedFolder.key : undefined,
        name: formValue.name,
        description: formValue.description,
        reportType,
      };

      if (this.dialogConfig.selectInitialPeriod) {
        finalValue.periodType = formValue.periodType;
        finalValue.period = formValue.period;
      }

      if (!!this.dialogConfig.geoFields) {
        finalValue['geoFields'] = this.dialogConfig.geoFields.map(field => {
          if (
            this.dialogConfig.singleGeoFields &&
            (field.key === GeoFieldKeys.MARKETS || field.key === GeoFieldKeys.SUB_MARKET)
          ) {
            return [formValue[field.key]];
          }
          return formValue[field.key];
        });
      }

      if (!!this.dialogConfig.statuses) {
        finalValue.statuses = formValue.statuses;
      }

      if (!!this.dialogConfig.statisticals) {
        finalValue.statisticals = formValue.statisticals;
      }

      if (!!this.dialogConfig.sizeMin) {
        finalValue.sizeMin = formValue.sizeMin;
      }

      if (!!this.dialogConfig.sizeMax) {
        finalValue.sizeMax = formValue.sizeMax;
      }

      if (!!this.dialogConfig.date) {
        const { from: fromConfig, to: toConfig } = this.dialogConfig.date;
        const { from: fromVal, to: toVal } = formValue.date;

        finalValue.date = {
          from: fromVal != null && fromConfig.postProcessFn != null ? fromConfig.postProcessFn(fromVal) : fromVal,
          to: toVal != null && toConfig.postProcessFn != null ? toConfig.postProcessFn(toVal) : toVal,
        };
      }

      if (!!this.dialogConfig.selectReportType) {
        for (const type in ReportTypes) {
          if (ReportTypes[type] === formValue.reportType) {
            finalValue.reportType = +type;
            break;
          }
        }
      }

      if (this.dialogConfig.selectReportEntity) {
        finalValue.reportEntityType = formValue.reportType;
      }

      this.dialogRef.close(finalValue);
    } else {
      this.searchFormGroup.controls['name'].setErrors({ incorrect: true });
      this.showEmptyInputError = true;
    }
  }

  public clearValue(controlName: string | string[]) {
    const field: UntypedFormControl = this.searchFormGroup.get(controlName) as UntypedFormControl;
    field.reset();
  }

  public periodTypeChange() {
    const today = new Date();
    const maxYear = today.getFullYear();
    const changedFromYear = this.periodType === QuarterSelectorType.Yearly;
    this.periodType = this.periodTypeControl.value;
    let val = this.period.value;
    if (val == null || (!val?.eq && !val?.lte && !val?.gte)) {
      const defaultYearlyValue = { periodValue: 1, periodYear: today.getFullYear() };
      val = { eq: defaultYearlyValue, lte: null, gte: null };
      if (!this.historicalStatChanges) {
        val = { eq: null, lte: defaultYearlyValue, gte: defaultYearlyValue };
      }
    }
    updateValues(val?.eq, this.periodTypeControl.value, maxYear, today, changedFromYear);
    updateValues(val?.gte, this.periodTypeControl.value, maxYear, today, changedFromYear);
    updateValues(val?.lte, this.periodTypeControl.value, maxYear, today, changedFromYear);
    if (this.historicalStatChanges) {
      this.appStorageService.setItem('periodType', this.periodTypeControl.value);
    }
    this.maxDate = today;
    this.period.setValue(val);
  }

  /*
   * This method will fetch the data of geography attributes which have @{onload and uri) keys
   * means which required to load on ngOnInit by hitting http uri point
   * And the geography attributes which have already data and requires to assign data on ngOnInit to drop downs
   */
  private initGeoValues() {
    this.dialogConfig.geoFields.forEach((geo, index) => {
      const geoField: ReportFieldsOptions = this.dialogConfig.geoFields[index];
      if (!!geo.uri && !!geo.onload) {
        this.getGeoData(geoField, []);
      } else if (!!geo.data && !!geo.onload) {
        if (geoField.key === GeoFieldKeys.PROPERTY_TYPES) {
          const options = geo.data;
          geoField.options = options.map(option => ({
            label: getPropertyTypeLabel(option.label),
            value: option.value,
          }));
        } else {
          geoField.options = geo.data;
        }
      }
    });
  }

  /*
   * This method will fetch the geography attributes data of a user and
   * will construct the data in required format to display in Prime Multi Select drop down list
   */
  private getGeoData(geoField, selectedGeoIds: number[]) {
    this.lookupCategoryService.targetPost(geoField.uri, selectedGeoIds).subscribe(data => {
      geoField.options = data.map(({ key, value }) => ({
        label: value.toUpperCase(),
        value: { key, value },
      }));
    });
  }

  private findDependentGeoIndex(index: number) {
    return this.dialogConfig.geoFields.findIndex(geo => this.dialogConfig.geoFields[index].dependent === geo.key);
  }
}
